import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpSubmitEntityRequest, useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { SaleLotCategory } from "./LotCategoryData";
import { SaleLot } from "../LotData";
import { LotDataGetLotCategoriesQuery } from "./LotCategoryQueries";
import { waitOnQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import { useLotDataGetSaleLotsQuery } from "../LotDataQueries";
import { Index } from "@agtech/core/utilities/AgTechUtilities";

/// Lot Category Actions

export const useLotCategoriesWriteCategoryAction = (): AgTechDataAction<SaleLotCategory, SaleLotCategory> => {
    let lotCategorySubmitRequest = useAgTechHttpSubmitEntityRequest<SaleLotCategory>();

    return {
        name: 'WriteLotCategory',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.id ? 'Updating lot category...' : 'Creating lot category...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.id ? 'updated' : 'created'} lot categry`
        }),
        action: async props => {
            return await lotCategorySubmitRequest.post({
                path: 'LotCategory',
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: LotDataGetLotCategoriesQuery,
                update: categories => {
                    return {
                        ...categories,
                        [props.responseData.id]: props.responseData
                    };
                }
            });
        }
    }
}

export type LotCategoryDeleteActionDTO = {
    category: SaleLotCategory
}

export const useLotCategoriesDeleteCategoryAction = (): AgTechDataAction<LotCategoryDeleteActionDTO, any, SaleLot[]> => {
    const getLotsQuery = useLotDataGetSaleLotsQuery();
    const deleteLotCategoryHttpRequest = useAgTechHttpPostRequest<SaleLotCategory, boolean>();

    return {
        name: 'DeleteLotCategory',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.category.id ? 'Deleting lot category...' : 'Deleting lot category...',
            actionConfirmationMessage: `Successfully deleted lot category`
        }),
        validate: async (props) => {
            const lotIndex = await waitOnQuery(getLotsQuery);

            const lotsWithCategory = Object.values(lotIndex)
                .filter(lot => lot.lotCategoryId === props.submittedEntity.category.id);

            props.validation.failWithWarningIf(
                !!lotsWithCategory && lotsWithCategory.length > 0,
                'The selected lot category is associated with one or more lots in this sale and cannot be deleted.'
            );
        },
        confirm: async props => {
            return props.appContext.confirmations.askForConfirmation({
                content: {
                    header: 'Are you sure you want to delete this lot category?',
                    details: 'All lots assigned to this category will have their category removed. This may impact reporting.'
                }
            });
        },
        action: async props => {
            return await deleteLotCategoryHttpRequest.post({
                path: 'LotCategory/Delete',
                postedData: props.submittedEntity.category
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: LotDataGetLotCategoriesQuery,
                update: categories => {
                    const updatedCategories = Object.values(categories)
                        .filter(cat => cat.id !== props.submittedEntity.category.id);

                    return Index(updatedCategories, cat => cat.id);
                }
            })

            props.appContext.popups.closeAll();
        }
    }
}